import type * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'

/**
 * Pagination is carried by `AWSJSON`, which crosses the API as a JSON string
 * rather than as an object, so an entity sent as-is is rejected during variable
 * coercion before the mutation ever runs.
 */
const PAGINATION =
  graphql.transformers.IN.stringify<ents.core.integrations.Pagination>()

/** Converts an entity API source to its GraphQL input representation. */
function apiSource(
  source: Readonly<ents.core.integrations.ApiSource>,
): graphql.schema.IntgApiSourceInput {
  return {
    pagination: PAGINATION.getGqlFrom(source.pagination),
    path: source.path,
    query: source.query
      ? Object.entries(source.query).map(([name, value]) => ({ name, value }))
      : undefined,
    recordsPath: source.recordsPath,
    responseType:
      source.responseType === 'array'
        ? graphql.schema.IntgApiResponseType.Array
        : graphql.schema.IntgApiResponseType.Object,
  }
}

/** Converts an entity definition import to its GraphQL input representation. */
function definitionImport(
  input: Readonly<ents.core.integrations.Connector.Definition.Import>,
): graphql.schema.IntgConnectorDefinitionImportInput {
  return {
    api: apiSource(input.api),
    mapping: {
      fields: input.mapping.fields.map(field => ({
        reference: field.reference
          ? { entityId: field.reference.entityId }
          : undefined,
        source: field.source,
        target: field.target,
      })),
      modelId: input.mapping.modelId,
    },
  }
}

export const INPUT = graphql.transformers.IN.nonNull(
  graphql.transformers.IN.custom<
    ents.core.integrations.Connector.Definition.Input,
    graphql.schema.IntgConnectorDefinitionInput
  >(input => ({
    imports: input.imports.map(definitionImport),
    name: input.name,
  })),
)

export const UPDATES = graphql.transformers.IN.nonNull(
  graphql.transformers.IN.custom<
    ents.core.integrations.Connector.Definition.Updates,
    graphql.schema.IntgConnectorDefinitionUpdates
  >(updates => ({
    imports: updates.imports?.map(definitionImport),
    name: updates.name,
  })),
)
